import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Upload, X, Plus, ArrowLeft, Save, FileText, ShieldAlert, CheckCircle2, Truck, MapPin } from 'lucide-react';
import { api, formatApiError } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import SpeciesSelect from '../components/SpeciesSelect';
import MorphSelect from '../components/MorphSelect';

export default function CreateListingPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = !!id;

  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);

  const [form, setForm] = useState({
    title: '',
    category_id: '',
    species_id: '',
    description: '',
    price: '',
    sex: '',
    age: '',
    weight: '',
    hatch_date: '',
    genes: [],
    traits: [],
    feeding_info: '',
    temperament: '',
    shipping_methods: ['enbixos', 'pickup'],
    quantity: 1,
    images: [],
    video_url: '',
    internal_id: '',
    // New taxonomy fields
    category_slug: '',
    species_name: '',
    species_canonical: '',
    morph_name: '',
    morph_canonical: '',
    custom_species: '',
    custom_morph: '',
  });

  const [geneInput, setGeneInput] = useState('');
  const [traitInput, setTraitInput] = useState('');
  const [legalDocs, setLegalDocs] = useState([]);
  const [legalDeclaration, setLegalDeclaration] = useState(false);
  const [platformConsent, setPlatformConsent] = useState(false);
  const [docUploading, setDocUploading] = useState(null);

  const handleDocFileUpload = async (index, file) => {
    if (!file) return;
    setDocUploading(index);
    try {
      const result = await api.uploadFile(file);
      const fileUrl = `/api/files/${result.id}`;
      setLegalDocs(prev => prev.map((doc, i) =>
        i === index ? { ...doc, file_url: fileUrl, file_name: file.name } : doc
      ));
      toast.success('Documento subido');
    } catch (err) {
      toast.error('Error al subir documento');
    } finally {
      setDocUploading(null);
    }
  };

  const addLegalDoc = (docType) => {
    if (legalDocs.some(d => d.doc_type === docType)) {
      toast.error('Ya agregaste este tipo de documento');
      return;
    }
    setLegalDocs(prev => [...prev, { doc_type: docType, doc_number: '', file_url: '', file_name: '' }]);
  };

  const removeLegalDoc = (index) => {
    setLegalDocs(prev => prev.filter((_, i) => i !== index));
  };

  useEffect(() => {
    loadCategories();
    if (isEdit) {
      loadListing();
    }
  }, [id]);

  useEffect(() => {
    if (form.category_id) {
      const cat = categories.find(c => c.id === form.category_id);
      if (cat?.slug) {
        setForm(prev => ({ ...prev, category_slug: cat.slug }));
      }
      loadComplianceRules(form.category_id);
    }
  }, [form.category_id, categories]);

  const LEGAL_DOC_TYPES = {
    factura: "Factura de compra",
    nota_compra: "Nota de compra",
    carta_cesion: "Carta de cesión",
    certificado_criadero: "Certificado de criadero",
    registro_uma: "Registro UMA",
    registro_pimvs: "Registro PIMVS",
    cites: "Documento CITES",
    importacion_legal: "Permiso de importación legal",
    identificacion_clutch: "Identificación de camada/clutch",
    pedigree: "Pedigree / Lineage",
    microchip: "Certificado de microchip",
    anillo_identificacion: "Anillo de identificación",
    otro: "Otro documento",
  };

  const loadCategories = async () => {
    try {
      const data = await api.getCategories();
      setCategories(data);
    } catch (e) {
      console.error('Error loading categories:', e);
    }
  };

  const loadComplianceRules = async (categoryId) => {
    try {
      const cat = categories.find(c => c.id === categoryId);
      const slug = cat?.slug || 'default';
      const catMap = {
        lagartos: 'reptiles', pitones: 'reptiles', geckos: 'reptiles', boas: 'reptiles',
        culebras: 'reptiles', tortugas: 'reptiles', anfibios: 'reptiles',
        'tortugas-acuaticas': 'reptiles', peces: 'peces_comunes',
        invertebrados: 'invertebrados', veneno: 'veneno', 'accesorios-y-mas': 'accesorios',
      };
      const complianceSlug = catMap[slug] || 'default';
      const data = await api.getComplianceRules(complianceSlug);
      setComplianceRules(data.rules);
    } catch (e) {
      setComplianceRules(null);
    }
  };

  const loadListing = async () => {
    try {
      const data = await api.getListing(id);
      setForm({
        ...data,
        price: data.price.toString()
      });
    } catch (e) {
      toast.error('Anuncio no encontrado');
      navigate('/dashboard/anuncios');
    }
  };

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    setUploading(true);
    try {
      const uploadPromises = files.map(file => api.uploadFile(file));
      const results = await Promise.all(uploadPromises);
      const newUrls = results.map(r => `/api/files/${r.id}`);
      setForm(prev => ({ ...prev, images: [...prev.images, ...newUrls] }));
      toast.success('Imágenes subidas');
    } catch (e) {
      toast.error('Error al subir imágenes');
    } finally {
      setUploading(false);
    }
  };

  const removeImage = (index) => {
    setForm(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };

  const addGene = () => {
    if (geneInput.trim() && !form.genes.includes(geneInput.trim())) {
      setForm(prev => ({ ...prev, genes: [...prev.genes, geneInput.trim()] }));
      setGeneInput('');
    }
  };

  const removeGene = (gene) => {
    setForm(prev => ({ ...prev, genes: prev.genes.filter(g => g !== gene) }));
  };

  const addTrait = () => {
    if (traitInput.trim() && !form.traits.includes(traitInput.trim())) {
      setForm(prev => ({ ...prev, traits: [...prev.traits, traitInput.trim()] }));
      setTraitInput('');
    }
  };

  const removeTrait = (trait) => {
    setForm(prev => ({ ...prev, traits: prev.traits.filter(t => t !== trait) }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!form.title || !form.category_id || !form.description || !form.price) {
      toast.error('Completa los campos requeridos');
      return;
    }

    if (!legalDeclaration) {
      toast.error('Debes aceptar la declaración de responsabilidad legal');
      return;
    }

    if (!platformConsent) {
      toast.error('Debes aceptar el consentimiento de plataforma intermediaria');
      return;
    }

    // Legal docs mandatory for ALL listings
    if (legalDocs.length === 0) {
      toast.error('Debes adjuntar al menos un documento de legalidad');
      return;
    }

    // Verify every doc has a file attached
    const docsWithoutFile = legalDocs.filter(d => !d.file_url);
    if (docsWithoutFile.length > 0) {
      toast.error('Todos los documentos deben tener un archivo adjunto');
      return;
    }

    setLoading(true);
    try {
      const data = {
        ...form,
        price: parseFloat(form.price),
        quantity: parseInt(form.quantity) || 1,
        legal_docs: legalDocs.map(d => ({
          doc_type: d.doc_type,
          doc_number: d.doc_number || '',
          file_url: d.file_url
        }))
      };

      if (isEdit) {
        await api.updateListing(id, data);
        toast.success('Anuncio actualizado');
      } else {
        await api.createListing(data);
        toast.success('Anuncio creado');
      }
      navigate('/dashboard/anuncios');
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="create-listing-page">
      <div className="flex items-center gap-4 mb-8">
        <button onClick={() => navigate(-1)} className="p-2 rounded-lg hover:bg-white/5 text-zinc-400">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div>
          <h1 className="text-2xl md:text-3xl font-light tracking-tight text-zinc-100 font-[Outfit]">
            {isEdit ? 'Editar anuncio' : 'Nuevo anuncio'}
          </h1>
          <p className="text-zinc-500">
            {categories.find(c => c.id === form.category_id)?.slug === 'accesorios-y-mas'
              ? 'Completa la informacion de tu producto'
              : 'Completa la informacion de tu animal'}
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="max-w-4xl">
        {/* Images */}
        <div className="glass-card p-6 mb-6">
          <h2 className="text-lg font-medium text-zinc-100 mb-4">Fotos</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {form.images.map((url, i) => (
              <div key={url} className="relative aspect-square rounded-xl overflow-hidden bg-zinc-800">
                <img src={url} alt="" className="w-full h-full object-cover" />
                <button
                  type="button"
                  onClick={() => removeImage(i)}
                  className="absolute top-2 right-2 p-1 rounded-full bg-black/50 text-white hover:bg-black/70"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ))}
            <label className="aspect-square rounded-xl border-2 border-dashed border-white/10 hover:border-white/20 flex flex-col items-center justify-center cursor-pointer transition-colors">
              <Upload className="w-8 h-8 text-zinc-600 mb-2" />
              <span className="text-sm text-zinc-500">
                {uploading ? 'Subiendo...' : 'Agregar foto'}
              </span>
              <input
                type="file"
                accept="image/*"
                multiple
                onChange={handleImageUpload}
                className="hidden"
                disabled={uploading}
                data-testid="image-upload"
              />
            </label>
          </div>
        </div>

        {/* Basic Info */}
        <div className="glass-card p-6 mb-6">
          <h2 className="text-lg font-medium text-zinc-100 mb-4">Información básica</h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="md:col-span-2">
              <Label className="text-zinc-400 mb-2 block">Título *</Label>
              <Input
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="Ej: Ball Python Pastel Hembra"
                className="bg-white/5 border-white/10 text-zinc-100"
                required
                data-testid="listing-title"
              />
            </div>

            <div>
              <Label className="text-zinc-400 mb-2 block">Categoria *</Label>
              <Select value={form.category_id} onValueChange={(v) => {
                const cat = categories.find(c => c.id === v);
                setForm(prev => ({
                  ...prev,
                  category_id: v,
                  category_slug: cat?.slug || '',
                  species_id: '',
                  species_name: '',
                  species_canonical: '',
                  morph_name: '',
                  morph_canonical: '',
                  custom_species: '',
                  custom_morph: '',
                  genes: [],
                  traits: [],
                }));
              }}>
                <SelectTrigger className="bg-white/5 border-white/10 text-zinc-100" data-testid="listing-category">
                  <SelectValue placeholder="Seleccionar" />
                </SelectTrigger>
                <SelectContent className="bg-[#121215] border-white/10">
                  {categories.map(cat => (
                    <SelectItem key={cat.id} value={cat.id}>
                      {cat.name}
                      {cat.sensitivity === 'high' ? ' (Restringido)' : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <SpeciesSelect
              categorySlug={form.category_slug}
              value={form.species_canonical}
              displayValue={form.custom_species || form.species_name}
              onChange={(canonical, display, isCustom) => {
                setForm(prev => ({
                  ...prev,
                  species_canonical: canonical === 'custom' ? '' : canonical,
                  species_name: display,
                  custom_species: isCustom ? display : '',
                  morph_name: '',
                  morph_canonical: '',
                  custom_morph: '',
                  genes: [],
                  traits: [],
                }));
              }}
            />

            {categories.find(c => c.id === form.category_id)?.slug !== 'accesorios-y-mas' && (
              <MorphSelect
                speciesCanonical={form.species_canonical}
                value={form.morph_name}
                onChange={(display) => {
                  setForm(prev => ({
                    ...prev,
                    morph_name: display,
                    morph_canonical: display ? display.toLowerCase().replace(/\s+/g, '-') : '',
                  }));
                }}
              />
            )}

            <div>
              <Label className="text-zinc-400 mb-2 block">Precio (MXN) *</Label>
              <Input
                type="number"
                value={form.price}
                onChange={(e) => setForm({ ...form, price: e.target.value })}
                placeholder="0"
                className="bg-white/5 border-white/10 text-zinc-100"
                required
                data-testid="listing-price"
              />
            </div>

            <div>
              <Label className="text-zinc-400 mb-2 block">Cantidad</Label>
              <Input
                type="number"
                value={form.quantity}
                onChange={(e) => setForm({ ...form, quantity: e.target.value })}
                min="1"
                className="bg-white/5 border-white/10 text-zinc-100"
              />
            </div>

            <div className="md:col-span-2">
              <Label className="text-zinc-400 mb-2 block">Descripción *</Label>
              <Textarea
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="Describe al animal, su historia, temperamento..."
                className="bg-white/5 border-white/10 text-zinc-100 min-h-[120px]"
                required
                data-testid="listing-description"
              />
            </div>
          </div>
        </div>

        {/* Details - only for animals */}
        {categories.find(c => c.id === form.category_id)?.slug !== 'accesorios-y-mas' && (
        <div className="glass-card p-6 mb-6">
          <h2 className="text-lg font-medium text-zinc-100 mb-4">Detalles</h2>
          <div className="grid md:grid-cols-3 gap-6">
            <div>
              <Label className="text-zinc-400 mb-2 block">Sexo</Label>
              <Select value={form.sex} onValueChange={(v) => setForm({ ...form, sex: v })}>
                <SelectTrigger className="bg-white/5 border-white/10 text-zinc-100">
                  <SelectValue placeholder="Seleccionar" />
                </SelectTrigger>
                <SelectContent className="bg-[#121215] border-white/10">
                  <SelectItem value="macho">Macho</SelectItem>
                  <SelectItem value="hembra">Hembra</SelectItem>
                  <SelectItem value="desconocido">Desconocido</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-zinc-400 mb-2 block">Edad</Label>
              <Input
                value={form.age}
                onChange={(e) => setForm({ ...form, age: e.target.value })}
                placeholder="Ej: 6 meses"
                className="bg-white/5 border-white/10 text-zinc-100"
              />
            </div>

            <div>
              <Label className="text-zinc-400 mb-2 block">Peso</Label>
              <Input
                value={form.weight}
                onChange={(e) => setForm({ ...form, weight: e.target.value })}
                placeholder="Ej: 350g"
                className="bg-white/5 border-white/10 text-zinc-100"
              />
            </div>

            <div>
              <Label className="text-zinc-400 mb-2 block">Fecha de nacimiento</Label>
              <Input
                type="date"
                value={form.hatch_date}
                onChange={(e) => setForm({ ...form, hatch_date: e.target.value })}
                className="bg-white/5 border-white/10 text-zinc-100"
              />
            </div>

            <div>
              <Label className="text-zinc-400 mb-2 block">Temperamento</Label>
              <Input
                value={form.temperament}
                onChange={(e) => setForm({ ...form, temperament: e.target.value })}
                placeholder="Ej: Dócil, come bien"
                className="bg-white/5 border-white/10 text-zinc-100"
              />
            </div>

            <div>
              <Label className="text-zinc-400 mb-2 block">Info alimentación</Label>
              <Input
                value={form.feeding_info}
                onChange={(e) => setForm({ ...form, feeding_info: e.target.value })}
                placeholder="Ej: Ratones F/T cada 7 días"
                className="bg-white/5 border-white/10 text-zinc-100"
              />
            </div>
          </div>
        </div>
        )}

        {/* Genes & Traits - only for animals */}
        {categories.find(c => c.id === form.category_id)?.slug !== 'accesorios-y-mas' && (
        <div className="glass-card p-6 mb-6">
          <h2 className="text-lg font-medium text-zinc-100 mb-4">Genes y traits</h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <Label className="text-zinc-400 mb-2 block">Genes</Label>
              <div className="flex gap-2 mb-3">
                <Input
                  value={geneInput}
                  onChange={(e) => setGeneInput(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addGene())}
                  placeholder="Ej: Pastel, Pied"
                  className="bg-white/5 border-white/10 text-zinc-100"
                />
                <Button type="button" onClick={addGene} variant="outline" className="border-white/10">
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              <div className="flex flex-wrap gap-2">
                {form.genes.map((gene, i) => (
                  <span key={gene} className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-purple-500/10 border border-purple-500/20 text-purple-300 text-sm">
                    {gene}
                    <button type="button" onClick={() => removeGene(gene)}><X className="w-3 h-3" /></button>
                  </span>
                ))}
              </div>
            </div>

            <div>
              <Label className="text-zinc-400 mb-2 block">Traits</Label>
              <div className="flex gap-2 mb-3">
                <Input
                  value={traitInput}
                  onChange={(e) => setTraitInput(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addTrait())}
                  placeholder="Ej: Het Clown, 100% Het Pied"
                  className="bg-white/5 border-white/10 text-zinc-100"
                />
                <Button type="button" onClick={addTrait} variant="outline" className="border-white/10">
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              <div className="flex flex-wrap gap-2">
                {form.traits.map((trait) => (
                  <span key={trait} className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-300 text-sm">
                    {trait}
                    <button type="button" onClick={() => removeTrait(trait)}><X className="w-3 h-3" /></button>
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
        )}

        {/* Shipping */}
        <div className="glass-card p-6 mb-6">
          <h2 className="text-lg font-medium text-zinc-100 mb-4">Entrega</h2>
          <div className="flex flex-wrap gap-4">
            <label className={`flex items-center gap-3 cursor-pointer p-3 rounded-lg border transition-colors ${form.shipping_methods.includes('enbixos') ? 'border-emerald-500/30 bg-emerald-500/5' : 'border-white/10 bg-white/5'}`}>
              <input
                type="checkbox"
                checked={form.shipping_methods.includes('enbixos')}
                onChange={(e) => {
                  setForm(prev => ({
                    ...prev,
                    shipping_methods: e.target.checked
                      ? [...prev.shipping_methods, 'enbixos']
                      : prev.shipping_methods.filter(m => m !== 'enbixos'),
                  }));
                }}
                className="rounded border-white/20 bg-white/5 text-emerald-600"
                data-testid="shipping-enbixos"
              />
              <Truck className="w-4 h-4 text-zinc-400" />
              <span className="text-zinc-300">Envio por Enbixos</span>
            </label>
            <label className={`flex items-center gap-3 cursor-pointer p-3 rounded-lg border transition-colors ${form.shipping_methods.includes('pickup') ? 'border-emerald-500/30 bg-emerald-500/5' : 'border-white/10 bg-white/5'}`}>
              <input
                type="checkbox"
                checked={form.shipping_methods.includes('pickup')}
                onChange={(e) => {
                  setForm(prev => ({
                    ...prev,
                    shipping_methods: e.target.checked
                      ? [...prev.shipping_methods, 'pickup']
                      : prev.shipping_methods.filter(m => m !== 'pickup'),
                  }));
                }}
                className="rounded border-white/20 bg-white/5 text-emerald-600"
                data-testid="shipping-pickup"
              />
              <MapPin className="w-4 h-4 text-zinc-400" />
              <span className="text-zinc-300">Recoger en persona</span>
            </label>
          </div>
        </div>

        {/* Legal Documentation - MANDATORY */}
        <div className="glass-card p-6 mb-6" data-testid="legal-docs-section">
          <div className="flex items-center gap-3 mb-4">
            <FileText className="w-5 h-5 text-amber-400" />
            <h2 className="text-lg font-medium text-zinc-100">Documentación de legalidad</h2>
            <span className="text-xs bg-red-500/20 text-red-400 px-2 py-0.5 rounded-full border border-red-500/30">Obligatorio</span>
          </div>

          <div className="mb-4 p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
            <div className="flex items-start gap-2">
              <ShieldAlert className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" />
              <p className="text-xs text-amber-300">
                Sube al menos un documento que acredite la legal posesión del ejemplar. Este documento NO será visible para nadie hasta que un comprador complete la compra.
              </p>
            </div>
          </div>

          {/* Legal docs list */}
          {legalDocs.map((doc, idx) => (
            <div key={idx} className="p-4 rounded-lg bg-white/5 mb-3 border border-white/10">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <FileText className="w-4 h-4 text-zinc-400" />
                  <p className="text-sm font-medium text-zinc-200">{LEGAL_DOC_TYPES[doc.doc_type] || doc.doc_type}</p>
                </div>
                <button type="button" onClick={() => removeLegalDoc(idx)} className="text-zinc-500 hover:text-red-400 p-1" data-testid={`remove-doc-${idx}`}>
                  <X className="w-4 h-4" />
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                <div>
                  <Label className="text-xs text-zinc-500 mb-1">Folio / Número (opcional)</Label>
                  <Input
                    value={doc.doc_number}
                    onChange={(e) => setLegalDocs(prev => prev.map((d, i) => i === idx ? { ...d, doc_number: e.target.value } : d))}
                    placeholder="Número de documento"
                    className="bg-white/5 border-white/10 text-zinc-100 text-sm"
                    data-testid={`doc-number-${idx}`}
                  />
                </div>
                <div>
                  <Label className="text-xs text-zinc-500 mb-1">Archivo adjunto <span className="text-red-400">*</span></Label>
                  {doc.file_url ? (
                    <div className="flex items-center gap-2 p-2 rounded bg-emerald-500/10 border border-emerald-500/20">
                      <CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                      <span className="text-xs text-emerald-300 truncate flex-1">{doc.file_name || 'Archivo subido'}</span>
                      <button
                        type="button"
                        onClick={() => setLegalDocs(prev => prev.map((d, i) => i === idx ? { ...d, file_url: '', file_name: '' } : d))}
                        className="text-zinc-500 hover:text-red-400"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ) : (
                    <label className={`flex items-center gap-2 p-2 rounded border border-dashed cursor-pointer transition-colors ${docUploading === idx ? 'border-amber-500/40 bg-amber-500/5' : 'border-white/20 hover:border-white/40 bg-white/5'}`}>
                      <input
                        type="file"
                        accept=".pdf,.jpg,.jpeg,.png,.webp"
                        className="hidden"
                        onChange={(e) => handleDocFileUpload(idx, e.target.files[0])}
                        disabled={docUploading !== null}
                        data-testid={`doc-file-${idx}`}
                      />
                      {docUploading === idx ? (
                        <span className="text-xs text-amber-300 animate-pulse">Subiendo...</span>
                      ) : (
                        <>
                          <Upload className="w-4 h-4 text-zinc-400" />
                          <span className="text-xs text-zinc-400">Seleccionar archivo (PDF, JPG, PNG)</span>
                        </>
                      )}
                    </label>
                  )}
                </div>
              </div>
            </div>
          ))}

          {/* Add doc */}
          <div className="border border-dashed border-white/10 rounded-lg p-4 mt-3">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
              <Select onValueChange={(v) => addLegalDoc(v)}>
                <SelectTrigger className="bg-white/5 border-white/10 text-zinc-100" data-testid="legal-doc-type">
                  <SelectValue placeholder="Agregar documento..." />
                </SelectTrigger>
                <SelectContent className="bg-[#121215] border-white/10">
                  {Object.entries(LEGAL_DOC_TYPES).map(([key, label]) => (
                    <SelectItem key={key} value={key}>{label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <p className="text-xs text-zinc-500">Selecciona el tipo de documento. Debes adjuntar el archivo para poder publicar.</p>
          </div>

          {/* Legal Consent - Two mandatory checkboxes */}
          <div className="mt-6 space-y-4">
            <h3 className="text-sm font-medium text-zinc-200 flex items-center gap-2">
              <ShieldAlert className="w-4 h-4 text-amber-400" />
              Consentimiento legal obligatorio
            </h3>

            {/* Checkbox 1: Seller Liability */}
            <div className="p-4 rounded-lg bg-zinc-800/50 border border-white/5">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={legalDeclaration}
                  onChange={(e) => setLegalDeclaration(e.target.checked)}
                  className="mt-1 rounded border-white/20 bg-white/5 text-emerald-600 flex-shrink-0"
                  data-testid="legal-declaration-checkbox"
                />
                <p className="text-xs text-zinc-400 leading-relaxed">
                  Declaro bajo protesta de decir verdad que toda publicación, ejemplar, documento, certificado,
                  permiso, factura, pedigree, carta de cesión o evidencia subida a MorphMX es auténtica, legal
                  y cumple con la legislación mexicana aplicable. Reconozco que soy el único responsable legal,
                  civil, administrativa, fiscal y penalmente por cualquier incumplimiento, falsedad documental,
                  posesión ilegal, comercialización restringida o uso ilícito de la plataforma derivado de mis
                  publicaciones o transacciones.
                </p>
              </label>
              {legalDeclaration && (
                <div className="mt-2 flex items-center gap-1.5 text-emerald-400 ml-7">
                  <CheckCircle2 className="w-3.5 h-3.5" />
                  <span className="text-xs">Declaración aceptada</span>
                </div>
              )}
            </div>

            {/* Checkbox 2: Platform Intermediary */}
            <div className="p-4 rounded-lg bg-zinc-800/50 border border-white/5">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={platformConsent}
                  onChange={(e) => setPlatformConsent(e.target.checked)}
                  className="mt-1 rounded border-white/20 bg-white/5 text-emerald-600 flex-shrink-0"
                  data-testid="platform-consent-checkbox"
                />
                <p className="text-xs text-zinc-400 leading-relaxed">
                  Acepto y reconozco que MorphMX opera únicamente como plataforma intermediaria tecnológica
                  entre usuarios y no actúa como vendedor directo, propietario del ejemplar, importador ni
                  garante de la legalidad de los bienes ofertados por terceros. Asimismo, acepto que cualquier
                  uso indebido, fraudulento o ilícito de la plataforma será de mi exclusiva responsabilidad y
                  faculta a MorphMX para suspender mi cuenta, congelar operaciones, conservar evidencia y
                  cooperar con autoridades.
                </p>
              </label>
              {platformConsent && (
                <div className="mt-2 flex items-center gap-1.5 text-emerald-400 ml-7">
                  <CheckCircle2 className="w-3.5 h-3.5" />
                  <span className="text-xs">Consentimiento aceptado</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Submit */}
        <div className="flex justify-end gap-4">
          <Button type="button" variant="outline" onClick={() => navigate(-1)} className="border-white/10 text-zinc-300">
            Cancelar
          </Button>
          <Button type="submit" disabled={loading} className="bg-zinc-100 text-zinc-900 hover:bg-white" data-testid="submit-listing">
            <Save className="w-4 h-4 mr-2" />
            {loading ? 'Guardando...' : isEdit ? 'Guardar cambios' : 'Publicar anuncio'}
          </Button>
        </div>
      </form>
    </div>
  );
}
